import {
  Box,
  Button,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Error } from 'components/Error'
import { Loading } from 'components/Loading'
import { TablePagination } from 'components/TablePagination'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useQueryState } from 'react-router-use-location-state'
import { useProcurementsSearchQuery } from 'services/procurements'
import {
  useProcurementSubmissionsItemsQuery,
  useProcurementSubmissionsQuery,
} from 'services/submissions/procurement-submission'
import { ISubmission } from 'types/submissions'
import _round from 'lodash/round'
import { FileUpload } from 'components/FileUpload'
import { Download } from '@mui/icons-material'
import { downloadExcel } from 'utils/download-file'

const StyledTable = styled(Table)(() => ({
  borderCollapse: 'collapse',
  width: '100%',
  tableLayout: 'fixed',

  '& th, & td': {
    border: '1px solid #dddddd !important',
  },
  '& th': {
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
}))

type Props = {
  submission?: ISubmission
  procurementRequirementId?: string
}

export const ProcurementEvaluationWorkrateTable = ({
  submission,
  procurementRequirementId,
}: Props) => {
  const { t } = useTranslation()
  const { tenderId } = useParams()
  const dispatch = useDispatch()

  const [page, setPage] = useQueryState('page', 0)
  const rowsPerPage = 10

  const args = {
    page,
    size: rowsPerPage,
    lotId: tenderId,
  } as const

  const { data, isLoading, isError } = useProcurementsSearchQuery(args, {
    refetchOnMountOrArgChange: true,
  })

  const procArgs = {
    page,
    size: rowsPerPage,
    id: submission?.procurementSubmission?.id!,
  } as const

  const {
    data: procSubmissionItemsData,
    isLoading: isProcSubmissionItemsLoading,
    isError: isProcSubmissionItemsError,
  } = useProcurementSubmissionsItemsQuery(procArgs, {
    skip: !submission,
    refetchOnMountOrArgChange: true,
  })

  const {
    data: procurementSubmission,
    isLoading: procurementSubmissionLoading,
    isError: procurementSubmissionError,
  } = useProcurementSubmissionsQuery(
    { id: submission?.procurementSubmission?.id! },
    {
      skip: !submission,
    }
  )

  const handleDownloadTemplate = async () => {
    downloadExcel({
      fileName: 'satinalma_predmeti_cedvel',
      url: `/procurement-submissions/${procurementSubmission?.id}/items/download-template`,
    })
  }

  if (isLoading || isProcSubmissionItemsLoading || procurementSubmissionLoading) {
    return <Loading fullPage={false} />
  }
  if (isError || isProcSubmissionItemsError || procurementSubmissionError) {
    return <Error fullPage={false} />
  }

  if (
    data === undefined ||
    procSubmissionItemsData === undefined ||
    procurementSubmission === undefined
  ) {
    return <></>
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6" fontWeight="bolder">
        {t('work-rate-sheet')}
      </Typography>
      <Box
        display="flex"
        justifyContent="flex-end"
        marginBottom={1}
        marginTop={1}
        gap={2}
      >
        <Button
          onClick={handleDownloadTemplate}
          variant="text"
          startIcon={<Download />}
        >
          {t('download-excel-template')}
        </Button>
      </Box>
      <TableContainer>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell>№</TableCell>
              <TableCell>{t('name')}</TableCell>
              <TableCell>{t('description')}</TableCell>
              <TableCell>{t('quantity')}</TableCell>
              <TableCell>{t('unit')}</TableCell>
              <TableCell>{t('price')}</TableCell>
              <TableCell>{t('sum-price')}</TableCell>
              <TableCell>{t('price-with-vat')}</TableCell>
              <TableCell>{t('file')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((item, index) => {
              const rowNumber = (data.info.currentPage - 1) * rowsPerPage + index + 1
              const procItem = procSubmissionItemsData.items.find(
                (i) => i.procurementRequirementItemId === item.id
              )
              return (
                <TableRow key={item.id}>
                  <TableCell>{rowNumber}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{item.measurement && t(item.measurement)}</TableCell>
                  <TableCell>{procItem?.price || ''}</TableCell>
                  <TableCell>{procItem?.totalPrice || ''}</TableCell>
                  <TableCell>
                    {Number(procItem?.vatTotalPrice) +
                      Number(procItem?.totalPrice) || ''}
                  </TableCell>
                  <TableCell>
                    <FileUpload
                      text={t('extra-document')}
                      fileId={item.attachedFileId}
                      handleFile={() => {}}
                      isDisabled
                      previewImage
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
          <TableBody>
            <TableRow>
              <TableCell align="right" colSpan={7}>
                {t('final-total-price')}
              </TableCell>
              <TableCell align="right">
                {procurementSubmission.totalPriceSum}
              </TableCell>
              <TableCell align="left">AZN</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right" colSpan={7}>
                {t('calculated-total-vat')}
              </TableCell>
              <TableCell align="right">
                {procurementSubmission.vatTotalPriceSum}
              </TableCell>
              <TableCell align="left">AZN</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right" colSpan={7}>
                {t('tender-submission-final-price')}
              </TableCell>
              <TableCell align="right">
                {_round(
                  Number(procurementSubmission.totalPriceSum) +
                    Number(procurementSubmission.vatTotalPriceSum),
                  2
                )}
              </TableCell>
              <TableCell align="left">AZN</TableCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
      {data && data.info.totalRows !== 0 && (
        <TablePagination
          total={data.info.totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={setPage}
        />
      )}
    </Stack>
  )
}
